/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useMemo, useState } from "react";
import { ToolbarTitle } from "../../../../ui/components/Toolbar/ToolbarTitle";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { ACTION_BUTTON_STYLE } from "../../../../ui/components/ActionButton";
import { DROPDOWN_STYLE, IDropdownMenuItem } from "../../../../ui/components/DropdownMenu";
import { TOOLBAR_BUTTON_SIZE, ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { ViewModel } from "../../Common/ViewModel";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { TGetBlueprintSchemaModel } from "@adapptio/lib-blueprint";
import {
	DataSourceAction,
	DataSourceVariable,
	DataSourceConstant,
	TBlueprintViewSchema,
	DataSourceMethod,
	DataSourceContextProvider,
	DataSourceContextConsumer,
	DataSourceWebSocketClient,
	DataSourceTimer
} from "@adapptio/lib-core";
import { ViewEditSelection } from "../../Common/ViewEditSelection";

function createAddMenu(
	model: TGetBlueprintSchemaModel<TBlueprintViewSchema>,
	selection: ViewEditSelection
): IDropdownMenuItem[] {
	const viewProps = model.props.spec.value.props;
	const dataSources = viewProps.dataSources.value;
	const paramsModel = viewProps.params;
	const paramsMapModel = paramsModel.props.props.props;
	const outletsModel = viewProps.outlets;
	const outletsMapModel = outletsModel.value.constant;

	const addDataSource = (type: string) => {
		const ds = dataSources.schema.addItem(
			dataSources,
			dataSources.items.length,
			{
				id: model.ctx.getUniqueIdentifier(type, true),
				type: type,
				opts: {}
			},
			true
		);

		selection.selectDataSource(ds);
	};

	const addParameter = () => {
		const parameter = paramsMapModel.schema.addElement(
			paramsMapModel,
			paramsMapModel.schema.getUniqueKey(paramsMapModel, "parameter", true),
			undefined,
			true
		);

		selection.selectParameter(paramsMapModel, parameter);
	};

	const addOutlet = () => {
		const outlet = outletsMapModel.schema.addElement(
			outletsMapModel,
			outletsMapModel.schema.getUniqueKey(outletsMapModel, "outlet", true),
			undefined,
			true
		);

		selection.selectOutlet(outletsMapModel, outlet);
	};

	return [
		{
			id: "action",
			label: "Add Data from Action",
			icon: DataSourceAction.icon,
			onClick: () => addDataSource(DataSourceAction.name)
		},
		{
			id: "variable",
			label: "Add State Variable",
			icon: DataSourceVariable.icon,
			onClick: () => addDataSource(DataSourceVariable.name)
		},
		{
			id: "constant",
			label: "Add Constant",
			icon: DataSourceConstant.icon,
			onClick: () => addDataSource(DataSourceConstant.name)
		},
		{
			id: "method",
			label: "Add Method",
			icon: DataSourceMethod.icon,
			onClick: () => addDataSource(DataSourceMethod.name)
		},
		{
			id: "contextProvider",
			label: "Add Context Provider",
			icon: DataSourceContextProvider.icon,
			onClick: () => addDataSource(DataSourceContextProvider.name)
		},
		{
			id: "contextConsumer",
			label: "Add Context Consumer",
			icon: DataSourceContextConsumer.icon,
			onClick: () => addDataSource(DataSourceContextConsumer.name)
		},
		{
			id: "webSocketClient",
			label: "Add WebSocket Client",
			icon: DataSourceWebSocketClient.icon,
			onClick: () => addDataSource(DataSourceWebSocketClient.name)
		},
		{
			id: "timer",
			label: "Add Timer",
			icon: DataSourceTimer.icon,
			onClick: () => addDataSource(DataSourceTimer.name)
		},
		{
			id: "parameter",
			label: "Add Parameter",
			icon: "mdi/record-circle-outline",
			onClick: addParameter
		},
		{
			id: "outlet",
			label: "Add Outlet",
			icon: "mdi/arrow-right-bold",
			onClick: addOutlet
		}
	];
}

export interface IViewEditorOutlineToolbarProps {
	model: ViewModel;
}

export const ViewEditorOutlineToolbar: React.FC<IViewEditorOutlineToolbarProps> = ({ model: viewModel }) => {
	const [ modelRef, setModelRef ] = useState(() => ({
		model: viewModel.getModel()
	}));

	const addMenuItems = useMemo(
		() => createAddMenu(modelRef?.model, viewModel.getSelection()),
		[ modelRef ]
	);

	useEffect(() => {
		const handleUpdate = () =>
			setModelRef({
				model: viewModel.getModel()
			});

		onEvent(viewModel.onRender, handleUpdate);

		return () => {
			offEvent(viewModel.onRender, handleUpdate);
		};
	}, [ viewModel ]);

	return (
		<>
			<ToolbarTitle>Outline</ToolbarTitle>
			<ToolbarSpacer grow={1} />
			<ToolbarButton
				icon="mdi/plus"
				size={TOOLBAR_BUTTON_SIZE.SMALL}
				menuItems={addMenuItems}
				menuStyle={DROPDOWN_STYLE.COMPACT}
				style={ACTION_BUTTON_STYLE.PRIMARY}
			/>
		</>
	);
};
